import { useState } from "react";
import { Copy, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { buildProjectEmail } from "@/lib/email-config";

interface CopyEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emailPrefix?: string;
}

export const CopyEmailModal = ({
  open,
  onOpenChange,
  emailPrefix = "ab349fsa",
}: CopyEmailModalProps) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const emailAddress = buildProjectEmail(emailPrefix);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(emailAddress);
      setCopied(true);
      toast({
        title: "Copied to clipboard",
        description: "Email address has been copied successfully.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try copying manually.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Your GreenLight Email Address</DialogTitle>
          <DialogDescription>
            Send draft emails from your ESP to this address to preview and gather feedback.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Email Address Display */}
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-xl border border-foreground/10 bg-muted/50 px-4 py-3">
              <p className="font-mono text-sm text-foreground break-all">
                {emailAddress}
              </p>
            </div>
            <Button
              onClick={handleCopy}
              variant="default"
              className="shrink-0 bg-foreground text-primary-foreground hover:bg-accent hover:text-accent-foreground"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </>
              )}
            </Button>
          </div>

          {/* Helper Text */}
          <p className="text-sm text-muted-foreground">
            Once you send an email to this address, it will appear here for your team to review and comment on.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
